import { BEACON_VERSION, CommunicationClient, Serializer, Logger, WCStorage, SDK_VERSION } from '@airgap/beacon-core';
import Client from '@walletconnect/sign-client';
import { getSdkError } from '@walletconnect/utils';
import { ActiveAccountUnspecified, ActiveNetworkUnspecified, InvalidNetworkOrAccount, InvalidReceivedSessionNamespace, InvalidSession, MissingRequiredScope, NotConnected } from '../error';
import { BeaconErrorType, BeaconMessageType, ExtendedWalletConnectPairingRequest, ExtendedWalletConnectPairingResponse, PermissionScope, StorageKey, TransportType } from '@airgap/beacon-types';
import { generateGUID, getAddressFromPublicKey, isPublicKeySC } from '@airgap/beacon-utils';
const TEZOS_PLACEHOLDER = 'tezos';
const BEACON_SDK_VERSION = 'beacon_sdk_version';
const logger = new Logger('WalletConnectCommunicationClient');
export var PermissionScopeMethods;
(function (PermissionScopeMethods) {
    PermissionScopeMethods["GET_ACCOUNTS"] = "tezos_getAccounts";
    PermissionScopeMethods["OPERATION_REQUEST"] = "tezos_send";
    PermissionScopeMethods["SIGN"] = "tezos_sign";
})(PermissionScopeMethods || (PermissionScopeMethods = {}));
export var PermissionScopeEvents;
(function (PermissionScopeEvents) {
    PermissionScopeEvents["CHAIN_CHANGED"] = "chainChanged";
    PermissionScopeEvents["ACCOUNTS_CHANGED"] = "accountsChanged";
    PermissionScopeEvents["REQUEST_ACKNOWLEDGED"] = "requestAcknowledged";
})(PermissionScopeEvents || (PermissionScopeEvents = {}));
function getStringBetween(str, startChar, endChar) {
    if (!str || !startChar || !endChar) {
        return '';
    }
    const startIndex = str.indexOf(startChar);
    const endIndex = str.indexOf(endChar, startIndex + 1);
    if (startIndex === -1 || endIndex === -1) {
        throw new Error('String not found');
    }
    return str.substring(startIndex + 1, endIndex);
}
export class WalletConnectCommunicationClient extends CommunicationClient {
    wcOptions;
    isLeader;
    activeListeners = new Map();
    channelOpeningListeners = new Map();
    static instance;
    signClient;
    storage = new WCStorage();
    session;
    activeAccountOrPbk;
    activeNetwork;
    disconnectionEvents = new Set();
    pingInterval;
    /**
     * this queue stores each active message id
     * [0] newest message
     * [length - 1] oldest message
     */
    messageIds = [];
    /**
     * Tracks the last time session extension was attempted (Unix timestamp in seconds)
     * Used to prevent duplicate re-extension attempts that can cause relay conflicts
     */
    lastExtensionAttempt = 0;
    constructor(wcOptions, isLeader) {
        super();
        this.wcOptions = wcOptions;
        this.isLeader = isLeader;
        this.storage.onMessageHandler = this.onStorageMessageHandler.bind(this);
        this.storage.onErrorHandler = this.onStorageErrorHandler.bind(this);
    }
    static getInstance(wcOptions, isLeader) {
        if (!this.instance) {
            this.instance = new WalletConnectCommunicationClient(wcOptions, isLeader);
        }
        return WalletConnectCommunicationClient.instance;
    }
    getTopicFromSession(session) {
        return session.topic;
    }
    async listenForEncryptedMessage(senderPublicKey, messageCallback) {
        if (this.activeListeners.has(senderPublicKey)) {
            return;
        }
        const callbackFunction = async (message) => {
            messageCallback(message);
        };
        this.activeListeners.set(senderPublicKey, callbackFunction);
    }
    async listenForChannelOpening(messageCallback) {
        const callbackFunction = async (pairingResponse) => {
            messageCallback(pairingResponse);
        };
        this.channelOpeningListeners.set('channelOpening', callbackFunction);
    }
    /**
     * WC Sign client doesn't sync between intances, meaning that a dApp signClient instance state may
     * differ from a wallet state
     */
    async refreshState() {
        await this.closeSignClient();
        const client = (await this.getSignClient());
        const lastIndex = client.session.keys.length - 1;
        if (lastIndex > -1) {
            const restoredSession = client.session.get(client.session.keys[lastIndex]);
            // Validate session is not stale
            if (await this.isSessionValid(restoredSession)) {
                this.session = restoredSession;
                this.updateStorageWallet(this.session);
                this.setDefaultAccountAndNetwork();
            }
            else {
                logger.warn('Restored session is stale during refresh, cleaning up');
                await this.cleanupStaleSession(restoredSession);
                this.clearState();
            }
        }
        else {
            this.clearState();
        }
    }
    clearEvents() {
        this.signClient?.removeAllListeners('session_event');
        this.signClient?.removeAllListeners('session_update');
        this.signClient?.removeAllListeners('session_delete');
        this.signClient?.removeAllListeners('session_expire');
        this.signClient?.removeAllListeners('session_extend');
        this.signClient?.removeAllListeners('proposal_expire');
        this.signClient?.core.pairing.events.removeAllListeners('pairing_delete');
        this.signClient?.core.pairing.events.removeAllListeners('pairing_expire');
    }
    onStorageMessageHandler(type) {
        if (!this.isMobileOS()) {
            return;
        }
        logger.debug('onStorageMessageHandler', type);
        if (type === 'RESET') {
            this.clearEvents();
            // no need to invoke `closeSignClinet` as the other tab already closed the connection
            this.signClient = undefined;
            this.clearState();
            this.messageIds = [];
            return;
        }
        this.refreshState();
    }
    onStorageErrorHandler(data) {
        logger.error('onStorageError', data);
    }
    async unsubscribeFromEncryptedMessages() {
        this.activeListeners.clear();
        this.channelOpeningListeners.clear();
    }
    async unsubscribeFromEncryptedMessage(_senderPublicKey) {
        // implementation
    }
    async closeSignClient() {
        if (!this.signClient) {
            logger.error('No client active');
            return;
        }
        await this.signClient.core.relayer.transportClose();
        this.signClient.core.events.removeAllListeners();
        this.signClient.core.relayer.events.removeAllListeners();
        this.signClient.core.heartbeat.stop();
        this.signClient.core.relayer.provider.events.removeAllListeners();
        this.signClient.core.relayer.subscriber.events.removeAllListeners();
        this.signClient.core.relayer.provider.connection.events.removeAllListeners();
        this.clearEvents();
        this.signClient = undefined;
    }
    async ping() {
        const client = await this.getSignClient();
        if (!client || !this.session) {
            logger.error('No session available.');
            return;
        }
        client
            .ping({ topic: this.session.topic })
            .then(() => {
            if (this.messageIds.length) {
                this.acknowledgeRequest(this.messageIds[0]);
            }
        })
            .catch((error) => {
            logger.error(`ping catch handler: ${error.message}`);
        })
            .then(() => {
            clearInterval(this.pingInterval);
            this.pingInterval = undefined;
        });
    }
    async checkWalletReadiness(_topic) {
        if (this.isMobileOS() || this.pingInterval) {
            return;
        }
        this.ping();
        this.pingInterval = setInterval(() => {
            this.ping();
        }, 30000);
    }
    async sendMessage(_message, _peer) {
        const serializer = new Serializer();
        const message = (await serializer.deserialize(_message));
        if (!message) {
            return;
        }
        this.messageIds.unshift(message.id);
        switch (message.type) {
            case BeaconMessageType.PermissionRequest:
                this.requestPermissions(message);
                break;
            case BeaconMessageType.OperationRequest:
                this.sendOperations(message);
                break;
            case BeaconMessageType.SignPayloadRequest:
                this.signPayload(message);
                break;
            default:
                return;
        }
    }
    async fetchAccounts(topic, chainId) {
        const signClient = await this.getSignClient();
        if (!signClient) {
            return;
        }
        return signClient.request({
            topic: topic,
            chainId: chainId,
            request: {
                method: PermissionScopeMethods.GET_ACCOUNTS,
                params: {}
            }
        });
    }
    async notifyListenersWithPermissionResponse(session, network, sessionEventId) {
        let publicKey;
        if (session.sessionProperties?.pubkey &&
            session.sessionProperties?.algo &&
            session.sessionProperties?.address) {
            publicKey = session.sessionProperties?.pubkey;
            logger.log('[requestPermissions]: Have pubkey in sessionProperties, skipping "get_accounts" call', session.sessionProperties);
        }
        else {
            const accounts = this.getTezosNamespace(session.namespaces).accounts;
            const addressOrPbk = accounts[0].split(':', 3)[2];
            if (isPublicKeySC(addressOrPbk)) {
                publicKey = addressOrPbk;
            }
            else {
                if (network.type !== this.wcOptions.network) {
                    throw new Error('Network in permission request is not the same as preferred network!');
                }
                const result = await this.fetchAccounts(session.topic, `${TEZOS_PLACEHOLDER}:${network.type}`);
                if (!result || result.length < 1) {
                    throw new Error('No account shared by wallet');
                }
                if (result.some((account) => !account.pubkey)) {
                    throw new Error('Public Key in `tezos_getAccounts` is empty!');
                }
                publicKey = result[0]?.pubkey;
            }
        }
        if (!publicKey) {
            throw new Error('Public Key in `tezos_getAccounts` is empty!');
        }
        const permissionResponse = {
            type: BeaconMessageType.PermissionResponse,
            appMetadata: {
                senderId: this.getTopicFromSession(session),
                name: session.peer.metadata.name,
                icon: session.peer.metadata.icons[0]
            },
            publicKey,
            network,
            scopes: [PermissionScope.SIGN, PermissionScope.OPERATION_REQUEST],
            id: sessionEventId ?? this.messageIds.pop() ?? '',
            walletType: 'implicit'
        };
        this.notifyListeners(this.getTopicFromSession(session), permissionResponse);
    }
    async requestPermissions(message) {
        logger.log('#### Requesting permissions');
        if (!this.getPermittedMethods().includes(PermissionScopeMethods.GET_ACCOUNTS)) {
            throw new MissingRequiredScope(PermissionScopeMethods.GET_ACCOUNTS);
        }
        if (this.activeAccountOrPbk) {
            try {
                await this.openSession();
            }
            catch (error) {
                logger.error(error.message);
                return;
            }
        }
        this.setDefaultAccountAndNetwork();
        this.notifyListenersWithPermissionResponse(this.getSession(), message.network);
    }
    /**
     * @description Once the session is establish, send payload to be approved and signed by the wallet.
     * @error MissingRequiredScope is thrown if permission to sign payload was not granted
     */
    async signPayload(signPayloadRequest) {
        const signClient = await this.getSignClient();
        if (!signClient) {
            return;
        }
        const session = this.getSession();
        if (!this.getPermittedMethods().includes(PermissionScopeMethods.SIGN)) {
            throw new MissingRequiredScope(PermissionScopeMethods.SIGN);
        }
        // Extend session if needed before making request
        await this.checkAndExtendSession();
        const network = this.getActiveNetwork();
        const account = await this.getAccountOrPK();
        this.validateNetworkAndAccount(network, account);
        this.checkWalletReadiness(this.getTopicFromSession(session));
        // TODO: Type
        signClient
            .request({
            topic: session.topic,
            chainId: `${TEZOS_PLACEHOLDER}:${network}`,
            request: {
                method: PermissionScopeMethods.SIGN,
                params: {
                    account: isPublicKeySC(account) ? await getAddressFromPublicKey(account) : account,
                    payload: signPayloadRequest.payload
                }
            }
        })
            .then((response) => {
            const signPayloadResponse = {
                type: BeaconMessageType.SignPayloadResponse,
                signingType: signPayloadRequest.signingType,
                signature: response?.signature,
                id: this.messageIds.pop()
            };
            this.notifyListeners(this.getTopicFromSession(session), signPayloadResponse);
            if (this.session && this.messageIds.length) {
                this.checkWalletReadiness(this.getTopicFromSession(session));
            }
        })
            .catch(async () => {
            const errorResponse = {
                type: BeaconMessageType.Error,
                id: this.messageIds.pop(),
                errorType: BeaconErrorType.ABORTED_ERROR
            };
            this.notifyListeners(this.getTopicFromSession(session), errorResponse);
            if (this.session && this.messageIds.length) {
                this.checkWalletReadiness(this.getTopicFromSession(session));
            }
        });
    }
    /**
     * @description Once the session is established, send Tezos operations to be approved, signed and inject by the wallet.
     * @error MissingRequiredScope is thrown if permission to send operation was not granted
     */
    async sendOperations(operationRequest) {
        const signClient = await this.getSignClient();
        if (!signClient) {
            return;
        }
        const session = this.getSession();
        if (!this.getPermittedMethods().includes(PermissionScopeMethods.OPERATION_REQUEST)) {
            throw new MissingRequiredScope(PermissionScopeMethods.OPERATION_REQUEST);
        }
        // Extend session if needed before making request
        await this.checkAndExtendSession();
        const network = this.getActiveNetwork();
        const account = await this.getAccountOrPK();
        this.validateNetworkAndAccount(network, account);
        this.checkWalletReadiness(this.getTopicFromSession(session));
        signClient
            .request({
            topic: session.topic,
            chainId: `${TEZOS_PLACEHOLDER}:${network}`,
            request: {
                method: PermissionScopeMethods.OPERATION_REQUEST,
                params: {
                    account: isPublicKeySC(account) ? await getAddressFromPublicKey(account) : account,
                    operations: operationRequest.operationDetails
                }
            }
        })
            .then((response) => {
            const sendOperationResponse = {
                type: BeaconMessageType.OperationResponse,
                transactionHash: response.operationHash ?? response.transactionHash ?? response.hash ?? '',
                id: this.messageIds.pop() ?? ''
            };
            this.notifyListeners(this.getTopicFromSession(session), sendOperationResponse);
            if (this.session && this.messageIds.length) {
                this.checkWalletReadiness(this.getTopicFromSession(session));
            }
        })
            .catch(async () => {
            const errorResponse = {
                type: BeaconMessageType.Error,
                id: this.messageIds.pop(),
                errorType: BeaconErrorType.ABORTED_ERROR
            };
            this.notifyListeners(this.getTopicFromSession(session), errorResponse);
            if (this.session && this.messageIds.length) {
                this.checkWalletReadiness(this.getTopicFromSession(session));
            }
        });
    }
    isMobileSesion(session) {
        const redirect = session.peer.metadata.redirect;
        return (!!redirect &&
            !!redirect.native &&
            !redirect.native.includes('http') &&
            !redirect.native.includes('ws'));
    }
    /**
     * Function used to fix appSwitching with web wallets when pairing through 'Other wallet flow'
     * @param session the newly created session
     */
    updateStorageWallet(session) {
        const selectedWallet = JSON.parse(localStorage.getItem(StorageKey.LAST_SELECTED_WALLET) ?? '{}');
        if (!selectedWallet.key) {
            return;
        }
        if (this.isMobileSesion(session)) {
            selectedWallet.type = 'mobile';
        }
        else {
            selectedWallet.type = 'web';
        }
        localStorage.setItem(StorageKey.LAST_SELECTED_WALLET, JSON.stringify(selectedWallet));
    }
    async init(forceNewConnection = false) {
        logger.warn('init');
        this.disconnectionEvents.size && this.disconnectionEvents.clear();
        if (forceNewConnection) {
            await this.closePairings();
        }
        const signClient = await this.getSignClient();
        if (!signClient) {
            throw new Error('Failed to connect to the relayer.');
        }
        const lastIndex = signClient.session.keys.length - 1;
        if (lastIndex > -1) {
            const restoredSession = signClient.session.get(signClient.session.keys[lastIndex]);
            // Validate session is not stale
            if (await this.isSessionValid(restoredSession)) {
                this.session = restoredSession;
                this.updateStorageWallet(this.session);
                this.setDefaultAccountAndNetwork();
                // Check if session needs extension (within 24 hours of expiry)
                await this.checkAndExtendSession();
                return undefined;
            }
            else {
                logger.warn('Restored session is stale, cleaning up and creating new connection');
                await this.cleanupStaleSession(restoredSession);
                // Fall through to create new connection below
            }
        }
        logger.warn('before create');
        const permissionScopeParams = {
            networks: [this.wcOptions.network],
            events: [],
            methods: [
                PermissionScopeMethods.GET_ACCOUNTS,
                PermissionScopeMethods.OPERATION_REQUEST,
                PermissionScopeMethods.SIGN
            ]
        };
        const optionalPermissionScopeParams = {
            networks: [this.wcOptions.network],
            events: [PermissionScopeEvents.REQUEST_ACKNOWLEDGED],
            methods: []
        };
        const connectParams = {
            requiredNamespaces: {
                [TEZOS_PLACEHOLDER]: this.permissionScopeParamsToNamespaces(permissionScopeParams)
            },
            optionalNamespaces: {
                [TEZOS_PLACEHOLDER]: this.permissionScopeParamsToNamespaces(optionalPermissionScopeParams)
            },
            sessionProperties: {
                [BEACON_SDK_VERSION]: SDK_VERSION
            }
        };
        const { uri, approval } = await Promise.race([
            signClient.connect(connectParams),
            new Promise((_, reject) => setTimeout(() => reject(new Error('The connection timed out.')), 60000))
        ]).catch((error) => {
            logger.error(`Init error: ${error.message}`);
            localStorage && localStorage.setItem(StorageKey.WC_INIT_ERROR, error.message);
            throw new Error(error.message);
        });
        // Extract topic from uri. Format is wc:topic@2...
        const topic = getStringBetween(uri, ':', '@');
        if (!topic) {
            return;
        }
        let hasResponse = false;
        signClient.core.pairing
            .ping({ topic })
            .then(async () => {
            if (!hasResponse) {
                // Only show "waiting for acknowledge" message if pong arrives before response
                const fun = this.eventHandlers.get("WC_ACK_NOTIFICATION" /* ClientEvents.WC_ACK_NOTIFICATION */);
                fun && fun('pending');
            }
        })
            .catch((err) => {
            console.error('--------', err);
        });
        approval()
            .then((session) => {
            logger.debug('session open');
            hasResponse = true;
            this.updateStorageWallet(session);
            const pairingResponse = new ExtendedWalletConnectPairingResponse(session.topic, session.peer.metadata.name, session.peer.publicKey, '3', session.topic, session.peer.metadata.name);
            this.channelOpeningListeners.forEach((listener) => {
                listener(pairingResponse);
            });
            if (session?.controller !== this.session?.controller) {
                logger.debug('Controller doesnt match, closing active session', [session.pairingTopic]);
                this.activeAccountOrPbk && this.closeActiveSession(this.activeAccountOrPbk, false);
                this.session = undefined; // close the previous session
            }
            // We need this check in the event the user aborts the sync process on the wallet side
            // but there is already a connection set
            this.session = this.session ?? session;
            logger.debug('Session is now', [session.pairingTopic]);
            this.validateReceivedNamespace(permissionScopeParams, this.session.namespaces);
        })
            .catch(async (error) => {
            hasResponse = true;
            if (!error.message ||
                !error.message.length ||
                error.message.toLowerCase().includes('expir')) {
                const fun = this.eventHandlers.get("CLOSE_ALERT" /* ClientEvents.CLOSE_ALERT */);
                fun && fun(TransportType.WALLETCONNECT);
                return;
            }
            logger.error('Error happened!', [error.message]);
            if (this.activeListeners.size === 0) {
                logger.debug('No active listeners', []);
                const fun = this.eventHandlers.get("WC_ACK_NOTIFICATION" /* ClientEvents.WC_ACK_NOTIFICATION */);
                fun && fun('error');
            }
            else {
                const _pairingTopic = topic ?? signClient.core.pairing.getPairings()[0]?.topic;
                logger.debug('New pairing topic?', []);
                const errorResponse = {
                    type: BeaconMessageType.Error,
                    id: this.messageIds.pop(),
                    errorType: BeaconErrorType.ABORTED_ERROR
                };
                this.notifyListeners(_pairingTopic, errorResponse);
            }
        })
            .then(async () => {
            const isLeader = await this.isLeader();
            if (!isLeader && !this.isMobileOS()) {
                await this.closeSignClient();
                this.clearState();
            }
        });
        logger.warn('return uri and topic');
        return { uri: uri ?? '', topic: topic };
    }
    async close() {
        this.storage.backup();
        await this.closePairings();
        this.unsubscribeFromEncryptedMessages();
        this.messageIds = [];
    }
    subscribeToSessionEvents(signClient) {
        // JWT validation error
        const baseHandler = signClient.core.relayer.onProviderErrorHandler;
        const handler = async (event) => {
            logger.error('subscribeToSessionEvents', event);
            if (event.message?.includes('JWT validation error')) {
                const fun = this.eventHandlers.get("ON_RELAYER_ERROR" /* ClientEvents.ON_RELAYER_ERROR */);
                fun && fun(TransportType.WALLETCONNECT);
            }
            await this.close();
            this.clearState();
            signClient.core.relayer.onProviderErrorHandler = baseHandler;
        };
        signClient.core.relayer.onProviderErrorHandler = handler;
        signClient.on('session_event', (event) => {
            if (event.params.event.name === PermissionScopeEvents.REQUEST_ACKNOWLEDGED &&
                this.messageIds.length) {
                this.acknowledgeRequest(this.messageIds[0]);
            }
        });
        signClient.on('session_update', (event) => {
            const session = signClient.session.get(event.topic);
            if (!session) {
                logger.warn('session_update', 'topic does not exist');
                return;
            }
            this.session = session;
            this.updateActiveAccount(this.getTezosNamespace(event.params.namespaces).accounts, session);
        });
        signClient.on('session_delete', (event) => {
            this.disconnectionEvents.add('session_delete');
            this.disconnect(signClient, { type: 'session', topic: event.topic });
        });
        signClient.on('session_expire', (event) => {
            this.disconnectionEvents.add('session_expire');
            this.disconnect(signClient, { type: 'session', topic: event.topic });
        });
        signClient.core.pairing.events.on('pairing_delete', (event) => {
            logger.debug('Pairing deleted', { topic: event.topic });
            // Don't disconnect session - pairings can be deleted while sessions remain active
            // Sessions communicate directly through the relay after initial pairing
        });
        signClient.core.pairing.events.on('pairing_expire', (event) => {
            logger.debug('Pairing expired', { topic: event.topic });
            // Don't disconnect session - pairings expire, but sessions can outlive them. Only disconnect on session_expire/session_delete
        });
        signClient.on('proposal_expire', (event) => {
            logger.debug('Session proposal expired', event.id);
        });
        signClient.on('session_extend', (event) => {
            logger.debug('Session extended', event.topic);
            // Session was extended by 7 days
            // Update our session reference if it matches
            if (this.session && this.session.topic === event.topic) {
                const updatedSession = signClient.session.get(event.topic);
                if (updatedSession) {
                    this.session = updatedSession;
                    logger.log('Active session extended, new expiry:', new Date(updatedSession.expiry * 1000));
                }
            }
        });
    }
    async acknowledgeRequest(id) {
        const session = this.getSession();
        const acknowledgeResponse = {
            type: BeaconMessageType.Acknowledge,
            id
        };
        this.notifyListeners(this.getTopicFromSession(session), acknowledgeResponse);
    }
    async updateActiveAccount(accounts, session) {
        try {
            if (accounts.length) {
                const [_namespace, chainId, addressOrPbk] = accounts[0].split(':', 3);
                const session = this.getSession();
                let publicKey;
                this.activeNetwork = chainId;
                this.activeAccountOrPbk = addressOrPbk;
                if (!isPublicKeySC(addressOrPbk)) {
                    const token = accounts[1] ? accounts[1].split(':', 3)[2] : '';
                    if (isPublicKeySC(token)) {
                        publicKey = token;
                    }
                    else {
                        const result = await this.fetchAccounts(session.topic, `${TEZOS_PLACEHOLDER}:${chainId}`);
                        publicKey = result?.find(({ address: _address }) => addressOrPbk === _address)?.pubkey;
                    }
                }
                else {
                    publicKey = addressOrPbk;
                }
                if (!publicKey) {
                    throw new Error('Public key for the new account not provided');
                }
                this.notifyListeners(this.getTopicFromSession(session), {
                    id: await generateGUID(),
                    type: BeaconMessageType.ChangeAccountRequest,
                    publicKey,
                    network: { type: chainId },
                    scopes: [PermissionScope.SIGN, PermissionScope.OPERATION_REQUEST],
                    walletType: 'implicit'
                });
            }
            else {
                this.notifyListenersWithPermissionResponse(session, {
                    type: this.wcOptions.network
                }, 'session_update');
            }
        }
        catch { }
    }
    async disconnect(signClient, trigger) {
        const session = await this.onSessionClosed(signClient, trigger.topic);
        if (!this.activeAccountOrPbk) {
            const fun = this.eventHandlers.get("RESET_STATE" /* ClientEvents.RESET_STATE */);
            fun && fun(TransportType.WALLETCONNECT);
        }
        if (!session) {
            return;
        }
        this.notifyListeners(this.getTopicFromSession(session), {
            id: await generateGUID(),
            type: BeaconMessageType.Disconnect
        });
        this.clearState();
    }
    async onSessionClosed(signClient, sessionTopic) {
        if (!this.session || this.session.topic !== sessionTopic) {
            return undefined;
        }
        try {
            // todo close the matching session and not just the first one
            if (!this.session.pairingTopic) {
                await signClient.core.pairing.disconnect({
                    topic: signClient.core.pairing.getPairings()[0]?.topic
                });
            }
            else {
                await signClient.core.pairing.disconnect({ topic: this.session.pairingTopic });
            }
        }
        catch (error) {
            // If the pairing was already closed, `disconnect` will throw an error.
            logger.warn(error.message);
        }
        return this.session;
    }
    async getPairingRequestInfo() {
        let _uri = '', _topic = '';
        try {
            logger.warn('getPairingRequestInfo');
            const { uri, topic } = (await this.init(true)) ?? { uri: '', topic: '' };
            _uri = uri;
            _topic = topic;
        }
        catch (error) {
            console.warn(error.message);
        }
        return new ExtendedWalletConnectPairingRequest(_topic, 'WalletConnect', await generateGUID(), BEACON_VERSION, await generateGUID(), _uri);
    }
    async closePairings() {
        if (!this.signClient) {
            return;
        }
        await this.closeSessions();
        const signClient = (await this.getSignClient());
        const pairings = signClient.pairing.getAll() ?? [];
        pairings.length &&
            (await Promise.allSettled(pairings.map((pairing) => signClient.disconnect({
                topic: pairing.topic,
                reason: {
                    code: 0, // TODO: Use constants
                    message: 'Force new connection'
                }
            }))));
        await this.closeSignClient();
        this.isMobileOS() && (await this.storage.resetState());
        this.isMobileOS() && this.storage.notify('RESET');
    }
    async closeSessions() {
        if (!this.signClient) {
            return;
        }
        const signClient = (await this.getSignClient());
        const sessions = signClient.session.getAll() ?? [];
        sessions.length &&
            (await Promise.allSettled(sessions.map((session) => signClient.disconnect({
                topic: session.topic,
                reason: {
                    code: 0, // TODO: Use constants
                    message: 'Force new connection'
                }
            }))));
        this.clearState();
    }
    async openSession() {
        const signClient = (await this.getSignClient());
        const pairingTopic = signClient.core.pairing.getPairings()[0]?.topic;
        logger.debug('Starting open session with', [pairingTopic]);
        if (!signClient) {
            throw new Error('Transport error.');
        }
        const permissionScopeParams = {
            networks: [this.wcOptions.network],
            events: [],
            methods: [
                PermissionScopeMethods.GET_ACCOUNTS,
                PermissionScopeMethods.OPERATION_REQUEST,
                PermissionScopeMethods.SIGN
            ]
        };
        const optionalPermissionScopeParams = {
            networks: [this.wcOptions.network],
            events: [PermissionScopeEvents.REQUEST_ACKNOWLEDGED],
            methods: []
        };
        const connectParams = {
            requiredNamespaces: {
                [TEZOS_PLACEHOLDER]: this.permissionScopeParamsToNamespaces(permissionScopeParams)
            },
            optionalNamespaces: {
                [TEZOS_PLACEHOLDER]: this.permissionScopeParamsToNamespaces(optionalPermissionScopeParams)
            },
            sessionProperties: {
                [BEACON_SDK_VERSION]: SDK_VERSION
            },
            pairingTopic
        };
        logger.debug('Checking wallet readiness', [pairingTopic]);
        this.checkWalletReadiness(pairingTopic);
        try {
            logger.debug('connect', [pairingTopic]);
            const { approval } = await Promise.race([
                signClient.connect(connectParams),
                new Promise((_, reject) => setTimeout(() => reject(new Error('The connection timed out.')), 60000))
            ]);
            logger.debug('before await approal', [pairingTopic]);
            const session = await approval();
            logger.debug('after await approal, have session', [pairingTopic]);
            // if I have successfully opened a session and I already have one opened
            if (session?.controller !== this.session?.controller) {
                logger.debug('Controller doesnt match, closing active session', [pairingTopic]);
                this.activeAccountOrPbk && this.closeActiveSession(this.activeAccountOrPbk, false);
                this.session = undefined; // close the previous session
            }
            // I still need this check in the event the user aborts the sync process on the wallet side
            // but there is already a connection set
            this.session = this.session ?? session;
            logger.debug('Session is now', [session.pairingTopic, pairingTopic]);
            this.validateReceivedNamespace(permissionScopeParams, this.session.namespaces);
        }
        catch (error) {
            if (!error.message ||
                !error.message.length ||
                error.message.toLowerCase().includes('expir')) {
                const fun = this.eventHandlers.get("CLOSE_ALERT" /* ClientEvents.CLOSE_ALERT */);
                fun && fun(TransportType.WALLETCONNECT);
            }
            else {
                logger.debug('Error happened!', [pairingTopic]);
                logger.error(error.message);
                if (this.activeListeners.size === 0) {
                    logger.debug('No active listeners', [pairingTopic]);
                    const fun = this.eventHandlers.get("WC_ACK_NOTIFICATION" /* ClientEvents.WC_ACK_NOTIFICATION */);
                    fun && fun('error');
                }
                else {
                    logger.debug('New pairing topic?', [pairingTopic]);
                    const errorResponse = {
                        type: BeaconMessageType.Error,
                        id: this.messageIds.pop(),
                        errorType: BeaconErrorType.ABORTED_ERROR
                    };
                    this.notifyListeners(pairingTopic, errorResponse);
                }
            }
        }
        if (this.session) {
            logger.debug('Have session, returning', [pairingTopic]);
            return this.session;
        }
        else {
            logger.debug('Nope, aborting', [pairingTopic]);
            throw new InvalidSession('No session set.' + pairingTopic);
        }
    }
    permissionScopeParamsToNamespaces(permissionScopeParams) {
        return {
            chains: permissionScopeParams.networks.map((network) => `${TEZOS_PLACEHOLDER}:${network}`),
            methods: permissionScopeParams.methods,
            events: permissionScopeParams.events ?? []
        };
    }
    validateReceivedNamespace(scope, receivedNamespaces) {
        if (receivedNamespaces[TEZOS_PLACEHOLDER]) {
            this.validateMethods(scope.methods, receivedNamespaces[TEZOS_PLACEHOLDER].methods);
            if (scope.events) {
                this.validateEvents(scope.events, receivedNamespaces['tezos'].events);
            }
            this.validateAccounts(scope.networks, receivedNamespaces[TEZOS_PLACEHOLDER].accounts);
        }
        else {
            this.clearState();
            throw new InvalidReceivedSessionNamespace('All namespaces must be approved', getSdkError('USER_REJECTED').code, 'incomplete', 'tezos');
        }
    }
    validateMethods(requiredMethods, receivedMethods) {
        const missingMethods = [];
        requiredMethods.forEach((method) => {
            if (!receivedMethods.includes(method)) {
                missingMethods.push(method);
            }
        });
        if (missingMethods.length > 0) {
            this.clearState();
            throw new InvalidReceivedSessionNamespace('All methods must be approved', getSdkError('USER_REJECTED_METHODS').code, 'incomplete', missingMethods);
        }
    }
    validateEvents(requiredEvents, receivedEvents) {
        const missingEvents = [];
        requiredEvents.forEach((method) => {
            if (!receivedEvents.includes(method)) {
                missingEvents.push(method);
            }
        });
        if (missingEvents.length > 0) {
            this.clearState();
            throw new InvalidReceivedSessionNamespace('All events must be approved', getSdkError('USER_REJECTED_EVENTS').code, 'incomplete', missingEvents);
        }
    }
    validateAccounts(requiredNetwork, receivedAccounts) {
        if (receivedAccounts.length === 0) {
            this.clearState();
            throw new InvalidReceivedSessionNamespace('Accounts must not be empty', getSdkError('USER_REJECTED_CHAINS').code, 'incomplete');
        }
        const receivedChains = [];
        const invalidChains = [];
        const missingChains = [];
        const invalidChainsNamespace = [];
        receivedAccounts.forEach((chain) => {
            const accountId = chain.split(':');
            if (accountId.length !== 3) {
                invalidChains.push(chain);
            }
            if (accountId[0] !== TEZOS_PLACEHOLDER) {
                invalidChainsNamespace.push(chain);
            }
            const network = accountId[1];
            if (!receivedChains.includes(network)) {
                receivedChains.push(network);
            }
        });
        if (invalidChains.length > 0) {
            this.clearState();
            throw new InvalidReceivedSessionNamespace('Accounts must be CAIP-10 compliant', getSdkError('USER_REJECTED_CHAINS').code, 'invalid', invalidChains);
        }
        if (invalidChainsNamespace.length > 0) {
            this.clearState();
            throw new InvalidReceivedSessionNamespace('Accounts must be defined in matching namespace', getSdkError('UNSUPPORTED_ACCOUNTS').code, 'invalid', invalidChainsNamespace);
        }
        requiredNetwork.forEach((network) => {
            if (!receivedChains.includes(network)) {
                missingChains.push(network);
            }
        });
        if (missingChains.length > 0) {
            this.clearState();
            throw new InvalidReceivedSessionNamespace('All chains must have at least one account', getSdkError('USER_REJECTED_CHAINS').code, 'incomplete', missingChains);
        }
    }
    async closeActiveSession(account, notify = true) {
        try {
            this.validateNetworkAndAccount(this.getActiveNetwork(), account);
        }
        catch (error) {
            logger.error(error.message);
            return;
        }
        const session = this.getSession();
        if (notify && this.messageIds.length) {
            const errorResponse = {
                type: BeaconMessageType.Disconnect,
                id: this.messageIds.pop(),
                errorType: BeaconErrorType.ABORTED_ERROR
            };
            this.notifyListeners(this.getTopicFromSession(session), errorResponse);
            this.messageIds = []; // reset
        }
        await this.signClient?.disconnect({
            topic: session.topic,
            reason: {
                code: 0, // TODO: Use constants
                message: 'Force new connection'
            }
        });
    }
    validateNetworkAndAccount(network, account) {
        if (!this.getTezosNamespace().accounts.includes(`${TEZOS_PLACEHOLDER}:${network}:${account}`)) {
            throw new InvalidNetworkOrAccount(network, account);
        }
    }
    /**
     * @description Access the active network
     * @error ActiveNetworkUnspecified thorwn when there are multiple Tezos networks in the session and none is set as the active one
     */
    getActiveNetwork() {
        if (!this.activeNetwork) {
            this.getSession();
            throw new ActiveNetworkUnspecified();
        }
        return this.activeNetwork;
    }
    setDefaultAccountAndNetwork() {
        const activeAccount = this.getAccounts();
        if (activeAccount.length) {
            this.activeAccountOrPbk = activeAccount[0];
        }
        const activeNetwork = this.getNetworks();
        if (activeNetwork.length) {
            this.activeNetwork = activeNetwork[0];
        }
    }
    /**
     * @description Return all connected accounts from the active session
     * @error NotConnected if no active session
     */
    getAccounts() {
        return this.getTezosNamespace().accounts.map((account) => account.split(':')[2]);
    }
    /**
     * @description Return all networks from the namespace of the active session
     * @error NotConnected if no active session
     */
    getNetworks() {
        return this.getPermittedNetwork();
    }
    getTezosNamespace(namespaces = this.getSession().namespaces) {
        if (TEZOS_PLACEHOLDER in namespaces) {
            return namespaces[TEZOS_PLACEHOLDER];
        }
        else {
            throw new InvalidSession('Tezos not found in namespaces');
        }
    }
    getPermittedMethods() {
        return this.getTezosRequiredNamespace().methods;
    }
    getPermittedNetwork() {
        return this.getTezosRequiredNamespace().chains.map((chain) => chain.split(':')[1]);
    }
    getTezosRequiredNamespace() {
        return {
            chains: [`${TEZOS_PLACEHOLDER}:${this.wcOptions.network}`],
            events: [],
            methods: ['tezos_getAccounts', 'tezos_send', 'tezos_sign']
        };
        // if (TEZOS_PLACEHOLDER in this.getSession().requiredNamespaces) {
        //   return this.getSession().requiredNamespaces[TEZOS_PLACEHOLDER] as {
        //     chains: string[]
        //     methods: string[]
        //     events: string[]
        //   }
        // } else {
        //   throw new InvalidSession('Tezos not found in requiredNamespaces')
        // }
    }
    async notifyListeners(topic, partialResponse) {
        const response = {
            ...partialResponse,
            version: '2',
            senderId: topic
        };
        const serializer = new Serializer();
        const serialized = await serializer.serialize(response);
        this.activeListeners.forEach((listener) => {
            listener(serialized);
        });
    }
    currentSession() {
        return this.session;
    }
    async tryConnectToRelayer() {
        const urls = new Set([
            this.wcOptions.opts.relayUrl,
            undefined,
            'wss://relay.walletconnect.com',
            'wss://relay.walletconnect.org',
            'wss://us-east-1.relay.walletconnect.com',
            'wss://eu-central-1.relay.walletconnect.com',
            'wss://ap-southeast-1.relay.walletconnect.com'
        ]);
        const errMessages = new Set();
        for (const relayUrl of urls) {
            try {
                return await Client.init({ ...this.wcOptions.opts, relayUrl });
            }
            catch (err) {
                errMessages.add(err.message);
                logger.warn(`Failed to connect to ${relayUrl}: ${err.message}`);
            }
        }
        throw new Error(`Failed to connect to relayer: ${Array.from(errMessages).join(',')}`);
    }
    async getSignClient() {
        if (this.signClient === undefined) {
            try {
                this.signClient = await this.tryConnectToRelayer();
                this.subscribeToSessionEvents(this.signClient);
            }
            catch (error) {
                logger.error(error.message);
                localStorage && localStorage.setItem(StorageKey.WC_INIT_ERROR, error.message);
                return undefined;
            }
        }
        return this.signClient;
    }
    getSession() {
        if (!this.session) {
            throw new NotConnected();
        }
        return this.session;
    }
    /**
     * @description Access the public key hash (or public key) of the active account
     * @error ActiveAccountUnspecified thrown when there are multiple Tezos account in the session and none is set as the active one
     */
    async getAccountOrPK() {
        if (!this.activeAccountOrPbk) {
            this.getSession();
            throw new ActiveAccountUnspecified();
        }
        return this.activeAccountOrPbk;
    }
    clearState() {
        this.session = undefined;
        this.activeAccountOrPbk = undefined;
        this.activeNetwork = undefined;
        this.lastExtensionAttempt = 0;
    }
    /**
     * @description Checks if session is close to expiry and extends it if needed
     * Extends session by 7 days if it will expire within 24 hours
     * Uses defensive error handling to prevent relay message conflicts (TrustWallet issue)
     */
    async checkAndExtendSession() {
        try {
            if (!this.session) {
                return;
            }
            const signClient = await this.getSignClient();
            if (!signClient) {
                return;
            }
            const ONE_HOUR_IN_SECONDS = 3600;
            const ONE_DAY_IN_SECONDS = 86400;
            const now = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = this.session.expiry - now;
            const timeSinceLastExtension = now - this.lastExtensionAttempt;
            // Skip if we recently attempted extension (within last hour)
            if (timeSinceLastExtension < ONE_HOUR_IN_SECONDS && this.lastExtensionAttempt > 0) {
                logger.debug('Skipping session extension - recently attempted', {
                    minutesSinceLastAttempt: (timeSinceLastExtension / 60).toFixed(1)
                });
                return;
            }
            // If session expires in the next 24 hours, extend it
            if (timeUntilExpiry < ONE_DAY_IN_SECONDS && timeUntilExpiry > 0) {
                logger.log('Session expires soon, extending...', {
                    topic: this.session.topic,
                    currentExpiry: new Date(this.session.expiry * 1000),
                    hoursRemaining: (timeUntilExpiry / 3600).toFixed(1)
                });
                // Update attempt timestamp before calling extend
                this.lastExtensionAttempt = now;
                try {
                    await signClient.extend({ topic: this.session.topic });
                    // Update our session reference with new expiry
                    const updatedSession = signClient.session.get(this.session.topic);
                    if (updatedSession) {
                        this.session = updatedSession;
                        logger.log('Session extended successfully', {
                            newExpiry: new Date(updatedSession.expiry * 1000)
                        });
                    }
                }
                catch (extendErr) {
                    // Log but don't throw - session might still be valid for a while
                    // This error may be harmless (e.g., "No matching key" from duplicate relay messages)
                    logger.warn('Session extension failed, continuing anyway', {
                        error: extendErr.message,
                        remainingHours: (timeUntilExpiry / 3600).toFixed(1)
                    });
                }
            }
        }
        catch (err) {
            // Catch any unexpected errors to ensure extension never blocks operations
            logger.error('Unexpected error in checkAndExtendSession', err.message);
        }
    }
    /**
     * @description Validates if a restored session is still valid and usable
     * @param session The session to validate
     * @returns true if session is valid, false if stale/expired
     */
    async isSessionValid(session) {
        // Check if Session is expired
        const now = Math.floor(Date.now() / 1000);
        if (session.expiry <= now) {
            logger.warn('Session validation failed: session expired', {
                expiry: session.expiry,
                now
            });
            return false;
        }
        // Check if Wallet is still responsive
        try {
            const signClient = await this.getSignClient();
            if (!signClient) {
                logger.warn('Session validation failed: no sign client available');
                return false;
            }
            await signClient.ping({ topic: session.topic });
            logger.debug('Session validation passed: session is valid');
            return true;
        }
        catch (err) {
            logger.warn('Session validation failed: ping failed', err.message);
            return false;
        }
    }
    /**
     * @description Cleans up a stale session by disconnecting and removing it
     * @param session The stale session to cleanup
     */
    async cleanupStaleSession(session) {
        try {
            const signClient = await this.getSignClient();
            if (signClient) {
                await signClient.disconnect({
                    topic: session.topic,
                    reason: getSdkError('USER_DISCONNECTED')
                });
            }
            logger.debug('Stale session cleaned up successfully', session.topic);
        }
        catch (err) {
            logger.warn('Error cleaning up stale session', err.message);
            // Continue even if cleanup fails - session is stale
        }
    }
}
//# sourceMappingURL=WalletConnectCommunicationClient.js.map